import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getUserProfile, createAssessment } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { EnergyGauge } from '@/components/assessment/EnergyGauge';
import { SymptomSelector } from '@/components/assessment/SymptomSelector';
import { SymptomAnalysis } from '@/components/ai/SymptomAnalysis';
import { Brain } from 'lucide-react';
import type { HDType } from '@/lib/types';
import { SYMPTOMS_BY_TYPE } from '@/lib/types';

export default function Assessment() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userType, setUserType] = useState<HDType | null>(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/barometre/login');
      return;
    }

    const loadUserProfile = async () => {
      try {
        if (currentUser.id) {
          const profile = await getUserProfile(currentUser.id);
          if (profile) {
            setUserType(profile.energyType);
          } else {
            toast({
              variant: "destructive",
              title: "Erreur",
              description: "Profil utilisateur non trouvé.",
            });
            navigate('/barometre/profile');
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger votre profil.",
        });
        navigate('/barometre/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser, navigate, toast]);

  const handleSymptomToggle = (symptom: string) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      } else {
        return [...prev, symptom];
      }
    });
  };

  const calculateEnergy = () => {
    if (!userType || selectedSymptoms.length === 0) return 70; // Default energy when no symptoms selected

    const symptoms = SYMPTOMS_BY_TYPE[userType];
    const selectedSymptomObjects = selectedSymptoms
      .filter(s => !s.startsWith('Autre : ')) // Exclude custom symptoms
      .map(text => symptoms.find(s => s.text === text))
      .filter((s): s is NonNullable<typeof s> => s !== undefined);

    if (selectedSymptomObjects.length === 0) return 70; // Default energy for custom symptoms only

    // Check for physical symptoms that indicate lower energy
    const hasPhysicalSymptoms = selectedSymptoms.some(symptom => 
      symptom.toLowerCase().includes('dos') ||
      symptom.toLowerCase().includes('tension') ||
      symptom.toLowerCase().includes('douleur') ||
      symptom.toLowerCase().includes('fatigue') ||
      symptom.toLowerCase().includes('lourdeur')
    );

    if (hasPhysicalSymptoms) {
      return Math.max(15, Math.min(40, 100 - (selectedSymptomObjects.length * 10)));
    }

    // Calculate energy based on symptom impacts
    const totalImpact = selectedSymptomObjects.reduce((sum, s) => sum + s.energyImpact, 0);
    const averageImpact = totalImpact / selectedSymptomObjects.length;
    
    // Convert impact (-10 to +10) to energy level (0 to 100)
    const energyLevel = Math.round(((averageImpact + 10) / 20) * 100);

    // If all symptoms are positive, return 100%
    const allPositive = selectedSymptomObjects.every(s => s.energyImpact > 0);
    if (allPositive) {
      return 100;
    }

    return Math.max(0, Math.min(100, energyLevel)); // Ensure energy level stays between 0 and 100
  };

  const getZone = (energy: number): 'green' | 'orange' | 'red' => {
    if (energy >= 70) return 'green';
    if (energy >= 40) return 'orange';
    return 'red';
  };

  const getResponses = (): string => {
    if (!userType || !SYMPTOMS_BY_TYPE[userType]) return '';

    const symptoms = SYMPTOMS_BY_TYPE[userType];
    const selectedSymptomObjects = selectedSymptoms.map(text => 
      symptoms.find(s => s.text === text)
    ).filter((s): s is NonNullable<typeof s> => s !== undefined);

    const negativeSymptoms = selectedSymptomObjects.filter(s => s.energyImpact < 0);
    const positiveSymptoms = selectedSymptomObjects.filter(s => s.energyImpact > 0);

    if (negativeSymptoms.length > 0) {
      return Array.from(new Set(negativeSymptoms.map(s => s.response))).join(' ');
    }

    return Array.from(new Set(positiveSymptoms.map(s => s.response))).join(' ');
  };

  const getAffectedCenters = () => {
    if (!userType || selectedSymptoms.length === 0) return [];
    
    const { affectedCenters } = analyzeEnergyCenters(userType, selectedSymptoms);
    return affectedCenters;
  };

  const handleSubmit = async () => {
    if (!currentUser?.id || !userType) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Session invalide. Veuillez vous reconnecter.",
      });
      navigate('/barometre/login');
      return;
    }

    setSubmitting(true);
    const energy = calculateEnergy();
    const zone = getZone(energy);
    const response = getResponses();
    const affectedCenters = getAffectedCenters();

    try {
      await createAssessment({
        userId: currentUser.id,
        zone,
        overallEnergy: energy,
        affectedCenters,
        symptoms: selectedSymptoms,
        response,
        notes,
      });

      toast({
        title: "Diagnostic enregistré",
        description: "Votre évaluation a été sauvegardée avec succès.",
      });
      navigate('/barometre/dashboard');
    } catch (error) {
      console.error('Error creating assessment:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'enregistrer le diagnostic.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const energy = calculateEnergy();
  const zone = getZone(energy);
  const response = getResponses();

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#54075B]" />
              Diagnostic énergétique
            </h1>
            <p className="text-gray-600">
              Évaluez votre état énergétique actuel
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Energy Gauge */}
            <div className="mb-8">
              <EnergyGauge value={energy} zone={zone} />
            </div>

            {/* Symptoms Selection */}
            {userType && (
              <SymptomSelector
                selectedSymptoms={selectedSymptoms}
                onSymptomToggle={handleSymptomToggle}
                energyType={userType}
                userId={currentUser!.id}
                showCustomForm={showCustomForm}
                onShowCustomForm={setShowCustomForm}
              />
            )}

            {/* AI Analysis */}
            {selectedSymptoms.length > 0 && userType && (
              <SymptomAnalysis
                symptoms={selectedSymptoms}
                energyType={userType}
                onSaveRecommendation={(recommendation) => {
                  setNotes(prev => prev ? `${prev}\n\nRecommandation IA :\n${recommendation}` : recommendation);
                }}
              />
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes personnelles (optionnel)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ajoutez des notes sur votre état actuel..."
                className="min-h-[100px]"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="w-full bg-[#54075B] hover:bg-[#54075B]/90"
              disabled={submitting || selectedSymptoms.length === 0}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer le diagnostic'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}